import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, ExternalLink } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge } from 'components/common/Badge';
import { Button } from 'components/common/Button';
import { Card, CardHeader } from 'components/common/Card';
import { LoadingScreen } from 'components/common/Spinner';
import { useStore } from 'context/StoreContext';
import { useApi } from 'hooks/useApi';
import { BillingInterval, BillingService } from 'services/billing/billing.service';
import { formatCurrency, formatDate } from 'utils/formatters';

const statusLabel = {
  trial: { text: 'Free trial', variant: 'info' },
  active: { text: 'Active', variant: 'success' },
  past_due: { text: 'Payment past due', variant: 'warning' },
  canceled: { text: 'Canceled', variant: 'danger' },
} as const;

const included = [
  'Alerts when supplier costs rise but shelf prices don\'t',
  'Suggested shelf prices that protect your margin',
  'Receipt and invoice photo capture',
  'POS sales import and margin reports',
  'Unlimited users',
];

export default function BillingPage() {
  const { org } = useStore();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [interval, setInterval] = useState<BillingInterval>('month');
  const [redirecting, setRedirecting] = useState(false);
  const [actionError, setActionError] = useState('');
  const { data: billing, loading, error } = useApi(() => BillingService.getSummary(org?.id ?? ''), [org?.id]);

  const go = async (action: () => Promise<string>) => {
    setRedirecting(true); setActionError('');
    try { window.location.assign(await action()); }
    catch (err: any) { setActionError(err?.message || 'Something went wrong. Please try again.'); setRedirecting(false); }
  };

  if (loading && !billing) return <LoadingScreen />;
  const isOwner = org?.role === 'owner';
  const stores = Math.max(billing?.active_stores ?? 1, 1);
  const status = billing ? statusLabel[billing.status] : null;

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-center gap-3">
        <Button aria-label="Back to settings" title="Back to settings" variant="ghost" size="sm" onClick={() => navigate('/settings')}><ArrowLeft size={16} /></Button>
        <h1 className="text-xl font-bold text-gray-900">Billing</h1>
      </div>

      {params.get('checkout') === 'success' && (
        <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          <CheckCircle size={16} /> Thanks! Your subscription is being activated. This page updates within a minute.
        </div>
      )}
      {(error || actionError) && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{actionError || error}</div>}

      {billing && status && (
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">Pellvr</p>
              <p className="mt-0.5 text-sm text-gray-500">
                {billing.has_subscription
                  ? `${billing.billed_stores} store${billing.billed_stores === 1 ? '' : 's'} · billed ${billing.billing_interval === 'year' ? 'annually' : 'monthly'}`
                  : `${stores} active store${stores === 1 ? '' : 's'}`}
              </p>
            </div>
            <Badge variant={status.variant}>{status.text}</Badge>
          </div>
          {billing.status === 'trial' && !billing.has_subscription && (
            <p className="mt-3 text-sm text-gray-600">
              {billing.trial_days_left > 0
                ? <>Your free trial ends in <strong>{billing.trial_days_left} day{billing.trial_days_left === 1 ? '' : 's'}</strong>{billing.trial_ends_at ? ` (${formatDate(billing.trial_ends_at)})` : ''}. Subscribe any time — you won't be charged until it ends.</>
                : <>Your free trial has ended. Subscribe to keep receiving profit-leak alerts.</>}
            </p>
          )}
          {billing.has_subscription && billing.current_period_end && (
            <p className="mt-3 text-sm text-gray-600">
              {billing.cancel_at_period_end ? 'Access ends' : 'Next renewal'} on {formatDate(billing.current_period_end)}.
            </p>
          )}
          {billing.status === 'past_due' && <p className="mt-3 text-sm text-amber-700">Your last payment didn't go through. Update your card to keep your alerts running.</p>}
          {billing.has_subscription && isOwner && (
            <Button className="mt-4" variant="secondary" loading={redirecting} onClick={() => go(() => BillingService.openPortal(org!.id))}>
              Manage billing <ExternalLink size={14} />
            </Button>
          )}
        </Card>
      )}

      {billing && !billing.has_subscription && (
        <Card>
          <CardHeader title="Subscribe" subtitle="One plan. Priced per store. Cancel any time." />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {(['month', 'year'] as const).map(option => (
              <button key={option} type="button" onClick={() => setInterval(option)}
                className={`rounded-lg border p-4 text-left transition-colors ${interval === option ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <p className="text-sm font-semibold text-gray-900">{option === 'month' ? 'Monthly' : 'Annual'}</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{formatCurrency(billing.price_per_store[option])}<span className="text-sm font-normal text-gray-500"> /store/mo</span></p>
                <p className="mt-1 text-xs text-gray-500">{option === 'year' ? `${formatCurrency(billing.price_per_store.year * 12)} per store, billed yearly — save 20%` : 'Billed monthly'}</p>
              </button>
            ))}
          </div>
          <ul className="mt-4 space-y-1.5">
            {included.map(item => <li key={item} className="flex items-start gap-2 text-sm text-gray-700"><CheckCircle size={15} className="mt-0.5 flex-shrink-0 text-green-600" />{item}</li>)}
          </ul>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
            <p className="text-sm text-gray-600">
              {stores} store{stores === 1 ? '' : 's'} × {formatCurrency(billing.price_per_store[interval])} = <strong className="text-gray-900">{formatCurrency(stores * billing.price_per_store[interval])}/mo</strong>
            </p>
            {!billing.billing_configured ? (
              <p className="text-sm text-gray-500">Online checkout isn't switched on yet. Email <a className="text-blue-600 hover:underline" href="mailto:support@pellvr.com">support@pellvr.com</a> to subscribe.</p>
            ) : isOwner ? (
              <Button loading={redirecting} onClick={() => go(() => BillingService.startCheckout(org!.id, interval))}>Continue to secure checkout</Button>
            ) : (
              <p className="text-sm text-gray-500">Only the account owner can subscribe.</p>
            )}
          </div>
          <p className="mt-3 text-xs text-gray-400">Payments are processed by Stripe. Pellvr never sees or stores your card number.</p>
        </Card>
      )}
    </div>
  );
}
